/**
 * Standings zone colors and helpers — shared between the standings page mapper
 * and the standings post-block.
 *
 * The Sports API returns rule keys per standings entry (e.g. "championsLeague",
 * "relegation"). We map each known key to:
 *   - a Tailwind background-color class used as a coloured strip on the row
 *   - a translation key under `labels.standings.rules.<key>`
 *
 * The rule key set is OPEN — the upstream API can introduce new keys at any
 * time. Unknown keys fall back to gray with the raw key shown as the label.
 *
 * Colour palette intent:
 *   - blue family   = continental top tier (UEFA Champions League, …)
 *   - green family  = continental second tier (UEFA Europa League, …)
 *   - yellow family = continental third tier (UEFA Conference League)
 *   - cyan family   = CAF competitions
 *   - sky family    = AFC competitions
 *   - teal family   = CONMEBOL competitions
 *   - emerald       = domestic promotion / domestic-league-stage promotion
 *   - red/orange    = relegation
 *   - purple family = knockout play-off rounds (1/16 → final)
 *   - violet family = "advances to next round" qualification paths
 *   - indigo        = domestic championship round (top group)
 *   - amber/stone   = winners / losers stage in split-format leagues
 *
 * To extend: hit `POST /api/standings/discover-rules` to see every rule key
 * the cached Sports API surface currently emits, pick a sensible bucket,
 * extend this map + add the label under `standings.rules.<key>` in every
 * locale file.
 */
import type { StatsTableLegendItem } from "fansunited-sports-ui";
import type { Labels } from "@/lib/labels/types";

export const ZONE_COLORS: Record<string, string> = {
    // UEFA — top tier
    championsLeague: "bg-blue-600",
    "championsLeagueQual.": "bg-blue-500",
    "championsLeague(w)": "bg-blue-500",
    "championsLeagueQual.(w)": "bg-blue-400",
    // UEFA — second tier
    europaLeague: "bg-green-500",
    "europaLeagueQual.": "bg-green-400",
    uefacup: "bg-green-500",
    europaClGroup: "bg-emerald-500",
    "europaClPlay-off": "bg-emerald-400",
    "europaCup(w)Qual.": "bg-emerald-300",
    // UEFA — third tier
    conferenceLeague: "bg-yellow-500",
    "uefaConferenceLeagueQual.": "bg-yellow-300",
    // CAF
    cafChampionsLeague: "bg-cyan-600",
    "cafChampionsLeagueQual.": "bg-cyan-500",
    cafConfederationCup: "bg-cyan-400",
    "cafConfederationsCupQual.": "bg-cyan-300",
    // AFC
    afcChampionsLeague: "bg-sky-600",
    "afcChampionsLeagueQual.": "bg-sky-500",
    afcChampionsLeague2: "bg-sky-400",
    "afcChampionsLeague2Qual.": "bg-sky-300",
    "afcChampionsLeague(w)": "bg-sky-500",
    afcChallengeLeague: "bg-sky-200",
    "afcChallengeLeagueQual.": "bg-sky-100",
    // CONMEBOL
    copaLibertadores: "bg-teal-600",
    "copaLibertadoresQual.": "bg-teal-500",
    copaSudamericana: "bg-teal-400",
    "copaSudamericanaQual.": "bg-teal-300",
    // World / international qualification
    worldCup: "bg-amber-600",
    "intercontinentalPlay-off": "bg-amber-400",
    nextRound: "bg-violet-500",
    possibleNextRound: "bg-violet-300",
    knockoutRound: "bg-violet-600",
    "rankingOfThird-placedTeams": "bg-violet-200",
    // Knockout play-off rounds (purple, deepening toward the final)
    playOffs: "bg-purple-500",
    possiblePlayOffs: "bg-purple-300",
    "playOffs-1/16Finals": "bg-purple-400",
    "playOffs-1/8Finals": "bg-purple-500",
    "playOffs-Quarter-finals": "bg-purple-600",
    "playOffs-Semi-finals": "bg-purple-700",
    "playOffs-Final": "bg-purple-800",
    // J. League position play-offs (uniform fuchsia — long tail, low priority)
    playOff3rdPlace: "bg-fuchsia-500",
    playOff5thPlace: "bg-fuchsia-500",
    playOff7thPlace: "bg-fuchsia-500",
    playOff9thPlace: "bg-fuchsia-500",
    playOff11thPlace: "bg-fuchsia-500",
    playOff13thPlace: "bg-fuchsia-500",
    playOff15thPlace: "bg-fuchsia-500",
    playOff17thPlace: "bg-fuchsia-500",
    playOff19thPlace: "bg-fuchsia-500",
    // Domestic promotion
    promotion: "bg-green-600",
    promotionPlayOffs: "bg-green-400",
    promotionGroup: "bg-emerald-600",
    "promotion-QuarterFinals": "bg-emerald-500",
    "promotion-SemiFinals": "bg-emerald-400",
    "promotion-Final": "bg-emerald-300",
    possiblePromotionPlayOff: "bg-emerald-200",
    // Domestic relegation
    relegation: "bg-red-500",
    relegationPlayOffs: "bg-red-400",
    relegationGroup: "bg-orange-500",
    playOut: "bg-red-300",
    // Domestic championship / placement
    championshipGroup: "bg-indigo-500",
    top6: "bg-indigo-400",
    "7th-10thPlaces": "bg-stone-400",
    placementGroup: "bg-stone-500",
    // Split-format winners / losers
    winnersStage: "bg-amber-500",
    losersStage: "bg-stone-500",
    // Trivial
    champion: "bg-yellow-600",
};

const FALLBACK_ZONE_COLOR = "bg-gray-500";

export interface ResolvedZone {
    color: string;
    label: string;
}

/**
 * Resolve a single rule key into a `{color, label}` zone descriptor.
 * Returns `undefined` for empty/missing input so callers can skip the row.
 */
export function resolveZone(ruleKey: string | undefined, ruleLabels: Labels["standings"]["rules"]): ResolvedZone | undefined {
    if (!ruleKey) return undefined;
    const label = (ruleLabels as Record<string, string | undefined>)[ruleKey] ?? ruleKey;
    const color = ZONE_COLORS[ruleKey] ?? FALLBACK_ZONE_COLOR;
    return { color, label };
}

/**
 * Build a deduped legend from every rule key actually seen on a set of rows.
 * Preserves the order in which keys first appeared so the legend reads top→bottom
 * naturally (qualification rows are typically encountered before relegation rows).
 */
export function buildLegend(ruleKeys: Iterable<string>, ruleLabels: Labels["standings"]["rules"]): StatsTableLegendItem[] {
    const seen = new Set<string>();
    const items: StatsTableLegendItem[] = [];
    for (const key of ruleKeys) {
        if (!key || seen.has(key)) continue;
        seen.add(key);
        const zone = resolveZone(key, ruleLabels);
        if (zone) items.push({ color: zone.color, label: zone.label });
    }
    return items;
}
